<script setup lang="ts">
/**
 * BCU Balance Widget (header)
 * Shows BCU balance with dropdown details.
 */
import { ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Zap, CreditCard } from 'lucide-vue-next'
import { api } from '@/core/api'
import { useAppEventsStore } from '@/stores/app-events'
import { logger } from '@/utils/logger'
import { useClickOutside } from '@/composables/useClickOutside'
import InfoTooltip from '@/components/info-tooltip/InfoTooltip.vue'

const { t } = useI18n()

const isOpen = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)

useClickOutside(wrapperRef, () => { isOpen.value = false })

const balance = ref<number | null>(null)
const used = ref(0)
const monthlyLimit = ref<number | null>(null)
const resetAt = ref<string | null>(null)
const plan = ref('free')
const balanceState = ref<'' | 'low-balance' | 'empty-balance' | 'usage-mode-active'>('')
const displayBalance = ref('--')
const displayLabel = ref('BCU')

const usageModeEnabled = ref(false)
const usageSpentBcu = ref(0)
const usageLimitBcu = ref<number | null>(null)

function formatBalance(b: number): string {
  if (b >= 1000) return (b / 1000).toFixed(1) + 'K'
  return b.toString()
}

const triggerClasses = computed(() => {
  const base = 'flex items-center gap-1.5 py-1.5 px-3 bg-surface-tertiary border border-border rounded-md text-[13px] cursor-pointer transition-all duration-200 font-inherit hover:bg-surface-hover hover:border-border-hover max-md:[&_.bcu-label]:hidden'
  const stateMap: Record<string, string> = {
    'low-balance': 'border-warning bg-warning/10 [&_.bcu-balance]:text-warning',
    'empty-balance': 'border-error bg-error/10 [&_.bcu-balance]:text-error',
    'usage-mode-active': 'border-accent-blue bg-accent-blue/10 [&_.bcu-balance]:text-accent-blue [&_.bcu-icon]:text-accent-blue',
  }
  return [base, stateMap[balanceState.value] || '']
})

async function loadBalance() {
  try {
    const data = await api.get<{
      balance: number
      used: number
      monthlyLimit: number | null
      resetAt: string | null
      plan: string
      usageModeEnabled?: boolean
      usageSpentThisPeriodBcu?: number
      usageMonthlyLimitBcu?: number | null
    }>('/auth/bcu')

    if (data && typeof data.balance === 'number') {
      balance.value = data.balance
      used.value = data.used || 0
      monthlyLimit.value = data.monthlyLimit
      resetAt.value = data.resetAt || null
      plan.value = data.plan || 'free'
      usageModeEnabled.value = !!data.usageModeEnabled
      usageSpentBcu.value = data.usageSpentThisPeriodBcu || 0
      usageLimitBcu.value = data.usageMonthlyLimitBcu ?? null

      balanceState.value = ''
      displayLabel.value = 'BCU'

      // When usage mode is enabled, always show spent / limit (not plan balance)
      if (data.usageModeEnabled) {
        balanceState.value = 'usage-mode-active'
        const spent = formatBalance(data.usageSpentThisPeriodBcu || 0)
        const limit = (data.usageMonthlyLimitBcu == null) ? '∞' : formatBalance(data.usageMonthlyLimitBcu)
        displayBalance.value = spent
        displayLabel.value = `/ ${limit} BCU`
      } else {
        displayBalance.value = formatBalance(data.balance)
        if (data.balance === 0) balanceState.value = 'empty-balance'
        else if (data.monthlyLimit && data.balance < data.monthlyLimit * 0.2) balanceState.value = 'low-balance'
      }
    }
  } catch (err) {
    logger.error('BcuWidget', 'Failed to load balance:', err)
    displayBalance.value = '--'
  }
}

const appEvents = useAppEventsStore()

watch(() => appEvents.bcuChangedAt, (val) => {
  if (val > 0) loadBalance()
})

onMounted(() => {
  loadBalance()
})

function usedPercent(): number {
  if (!monthlyLimit.value) return 0
  return Math.round((used.value / monthlyLimit.value) * 100)
}

function formatReset(): string {
  if (!resetAt.value) return '--'
  return new Date(resetAt.value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function progressFillClass(): string {
  const p = usedPercent()
  if (p >= 90) return 'bg-gradient-to-r from-error to-red-400'
  if (p >= 70) return 'bg-gradient-to-r from-yellow-500 to-amber-400'
  return 'bg-gradient-to-r from-brand to-brand-light'
}

function usageSpentPercent(): number {
  if (!usageLimitBcu.value) return 0
  return Math.round((usageSpentBcu.value / usageLimitBcu.value) * 100)
}

function usageProgressFillClass(): string {
  const p = usageSpentPercent()
  if (p >= 90) return 'bg-gradient-to-r from-error to-red-400'
  if (p >= 70) return 'bg-gradient-to-r from-yellow-500 to-amber-400'
  return 'bg-gradient-to-r from-accent-blue to-blue-400'
}

function planBadgeClass(): string {
  if (plan.value === 'master') return 'bg-gradient-to-br from-amber-500 to-orange-500 text-white!'
  if (plan.value === 'pro') return 'bg-gradient-to-br from-brand to-accent-purple text-white!'
  return 'bg-surface-tertiary text-foreground-secondary!'
}
</script>

<template>
  <div ref="wrapperRef" class="relative flex items-center">
    <button :class="triggerClasses" @click.stop="isOpen = !isOpen">
      <Zap class="bcu-icon text-brand shrink-0" :size="14" />
      <span class="bcu-balance font-semibold text-foreground">{{ displayBalance }}</span>
      <span class="bcu-label text-[11px] font-medium text-foreground-muted uppercase">{{ displayLabel }}</span>
    </button>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2.5"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2.5"
    >
      <div
        v-if="isOpen"
        class="absolute top-[calc(100%+8px)] right-0 max-md:right-[-20px] w-[280px] max-md:w-[260px] bg-surface border border-border rounded-xl shadow-lg z-[1000] overflow-hidden"
      >
        <!-- Header -->
        <div class="p-4 bg-gradient-to-br from-brand/[0.08] to-accent-purple/[0.08] border-b border-border">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[11px] font-semibold uppercase text-foreground-muted tracking-wide">
              {{ usageModeEnabled ? 'On-Demand Usage' : 'BCU Balance' }}
            </span>
            <InfoTooltip
              :content="usageModeEnabled
                ? 'On-demand mode: BCU are consumed beyond your plan limit and charged at $5 per 2,500 BCU.'
                : 'BCU (Board Compute Units) are credits used for AI-powered features like code review and context generation.'"
              position="bottom-right"
            />
          </div>
          <div v-if="usageModeEnabled" class="flex items-baseline gap-1.5">
            <span class="text-[28px] font-bold text-foreground tracking-tight">{{ usageSpentBcu.toLocaleString() }}</span>
            <span class="text-sm font-medium text-foreground-muted">/ {{ usageLimitBcu === null ? '∞' : usageLimitBcu.toLocaleString() }} BCU</span>
          </div>
          <div v-else class="flex items-baseline gap-1.5">
            <span class="text-[28px] font-bold text-foreground tracking-tight">{{ balance?.toLocaleString() ?? '--' }}</span>
            <span class="text-sm font-medium text-foreground-muted">BCU</span>
          </div>
        </div>

        <!-- Stats body -->
        <div class="p-4 flex flex-col gap-3">
          <!-- Usage mode stats -->
          <template v-if="usageModeEnabled">
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">Usage Spent</span>
              <span class="text-[13px] font-semibold text-foreground">{{ usageSpentBcu.toLocaleString() }} BCU</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">Usage Limit</span>
              <span class="text-[13px] font-semibold text-foreground">{{ usageLimitBcu === null ? 'Unlimited' : usageLimitBcu.toLocaleString() + ' BCU' }}</span>
            </div>
            <div v-if="usageLimitBcu !== null" class="flex items-center gap-2.5">
              <div class="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                <div
                  :class="['h-full rounded-full transition-[width] duration-300', usageProgressFillClass()]"
                  :style="{ width: Math.min(usageSpentPercent(), 100) + '%' }"
                />
              </div>
              <span class="text-xs font-semibold text-foreground-muted min-w-[36px] text-right">{{ usageSpentPercent() }}%</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">Plan Balance</span>
              <span class="text-[13px] font-semibold text-foreground-muted">{{ balance?.toLocaleString() ?? '0' }} BCU</span>
            </div>
          </template>
          <!-- Normal plan stats -->
          <template v-else>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">{{ t('bcu.usedThisMonth') }}</span>
              <span class="text-[13px] font-semibold text-foreground">{{ used.toLocaleString() }} BCU</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">{{ t('bcu.monthlyLimit') }}</span>
              <span class="text-[13px] font-semibold text-foreground">{{ monthlyLimit ? monthlyLimit.toLocaleString() + ' BCU' : 'Unlimited' }}</span>
            </div>
            <div class="flex items-center gap-2.5">
              <div class="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                <div
                  :class="['h-full rounded-full transition-[width] duration-300', progressFillClass()]"
                  :style="{ width: Math.min(usedPercent(), 100) + '%' }"
                />
              </div>
              <span class="text-xs font-semibold text-foreground-muted min-w-[36px] text-right">{{ usedPercent() }}%</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[13px] text-foreground-secondary">{{ t('bcu.resetsOn') }}</span>
              <span class="text-[13px] font-semibold text-foreground">{{ formatReset() }}</span>
            </div>
          </template>
          <div class="flex items-center justify-between">
            <span class="text-[13px] text-foreground-secondary">{{ t('bcu.currentPlan') }}</span>
            <span :class="['py-0.5 px-2 rounded text-[11px] font-semibold uppercase tracking-wide', planBadgeClass()]">{{ plan.toUpperCase() }}</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex flex-col gap-2 px-4 py-3 border-t border-border bg-surface-tertiary">
          <RouterLink
            to="/settings?tab=billing"
            class="flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-[13px] font-medium no-underline transition-all duration-150 bg-surface text-foreground border border-border hover:bg-surface-secondary hover:border-border-hover hover:no-underline"
            @click="isOpen = false"
          >
            <CreditCard :size="14" />
            Billing
          </RouterLink>
        </div>
      </div>
    </Transition>
  </div>
</template>
